import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface SupporterButtonProps {
  streamerId: string;
  streamerName: string;
  onAuthRequired?: () => void;
}

export default function SupporterButton({ streamerId, streamerName, onAuthRequired }: SupporterButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'supporter' | 'powerup'>('supporter');
  const [tiers, setTiers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Power-Up state
  const [powerUpAmount, setPowerUpAmount] = useState('');
  const [powerUpMessage, setPowerUpMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  useEffect(() => {
    if (showModal) {
      loadTiers();
    }
  }, [showModal]);

  const loadTiers = async () => {
    try {
      const { data, error } = await supabase
        .from('supporter_tiers')
        .select('*')
        .eq('streamer_id', streamerId)
        .order('tier_level');
      
      if (error) {
        console.error('Error loading tiers:', error);
        return;
      }
      
      if (data) setTiers(data);
    } catch (error) {
      console.error('Error loading tiers:', error);
    }
  };

  const handleSubscribe = async (tierId: string) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setShowModal(false);
        if (onAuthRequired) onAuthRequired();
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-supporter-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ tierId, streamerId }),
        }
      );

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned');
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleSendPowerUp = async () => {
    if (!powerUpAmount || parseFloat(powerUpAmount) < 1) {
      alert('Minimum Power-Up amount is $1');
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setShowModal(false);
        if (onAuthRequired) onAuthRequired();
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-power-up`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            receiverId: streamerId,
            amount: parseFloat(powerUpAmount),
            message: powerUpMessage,
            isAnonymous
          }),
        }
      );

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned');
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap"
      >
        <i className="ri-heart-fill mr-2"></i>
        Support
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                Support {streamerName}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => setActiveTab('supporter')}
                className={`flex-1 py-4 px-6 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'supporter'
                    ? 'text-purple-400 border-b-2 border-purple-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <i className="ri-vip-crown-line mr-2"></i>
                Become a Supporter
              </button>
              <button
                onClick={() => setActiveTab('powerup')}
                className={`flex-1 py-4 px-6 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'powerup'
                    ? 'text-yellow-400 border-b-2 border-yellow-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <i className="ri-flashlight-line mr-2"></i>
                Send Power-Up
              </button>
            </div>

            <div className="p-6">
              {activeTab === 'supporter' ? (
                <div>
                  <p className="text-gray-400 mb-6">
                    Choose a tier and get exclusive perks every month!
                  </p>
                  
                  {tiers.length === 0 ? (
                    <div className="text-center py-12">
                      <i className="ri-vip-crown-line text-gray-600 text-5xl mb-4"></i>
                      <p className="text-gray-400">No supporter tiers available yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {tiers.map((tier) => (
                        <div 
                          key={tier.id}
                          className="bg-gray-900 rounded-lg p-6 border-2 border-purple-600/30 hover:border-purple-600 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-white">{tier.tier_name}</h3>
                            <div className="text-2xl font-bold text-purple-400">
                              ${tier.price}<span className="text-sm text-gray-400">/month</span>
                            </div>
                          </div>
                          
                          <ul className="space-y-2 mb-6">
                            {tier.benefits.map((benefit: string, idx: number) => (
                              <li key={idx} className="text-gray-300 flex items-start">
                                <i className="ri-check-line text-purple-400 mr-2 mt-0.5"></i>
                                {benefit}
                              </li>
                            ))}
                          </ul>

                          <button
                            onClick={() => handleSubscribe(tier.id)}
                            disabled={loading}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? 'Processing...' : 'Subscribe Now'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-gray-400 mb-6">
                    Send a one-time Power-Up to show your support!
                  </p>

                  <div className="mb-6">
                    <label className="block text-white font-semibold mb-2">Amount</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">$</span>
                      <input
                        type="number"
                        value={powerUpAmount}
                        onChange={(e) => setPowerUpAmount(e.target.value)}
                        placeholder="5.00"
                        min="1"
                        step="0.01"
                        className="w-full bg-gray-900 text-white pl-8 pr-4 py-4 rounded-lg border border-gray-700 focus:border-yellow-600 focus:outline-none text-lg"
                      />
                    </div>
                    <div className="flex gap-2 mt-3">
                      {[5, 10, 25, 50, 100].map((amount) => (
                        <button
                          key={amount}
                          onClick={() => setPowerUpAmount(amount.toString())}
                          className="flex-1 bg-gray-900 hover:bg-gray-800 text-yellow-400 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-white font-semibold mb-2">Message (Optional)</label>
                    <textarea
                      value={powerUpMessage}
                      onChange={(e) => setPowerUpMessage(e.target.value)}
                      placeholder="Add a message to your Power-Up..."
                      maxLength={200}
                      rows={3}
                      className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-yellow-600 focus:outline-none resize-none text-sm"
                    />
                    <div className="text-right text-gray-500 text-sm mt-1">
                      {powerUpMessage.length}/200
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAnonymous}
                        onChange={(e) => setIsAnonymous(e.target.checked)}
                        className="w-5 h-5 text-yellow-600 bg-gray-900 border-gray-700 rounded focus:ring-yellow-600 cursor-pointer"
                      />
                      <span className="ml-3 text-white">Send anonymously</span>
                    </label>
                  </div>

                  <button
                    onClick={handleSendPowerUp}
                    disabled={loading || !powerUpAmount}
                    className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white py-4 rounded-lg text-lg font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ri-flashlight-fill mr-2"></i>
                        Send Power-Up
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
